var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { useCallback, useMemo } from "react";
import { useQueryClient } from "react-query";
import { useLegolandApiClient, useReleaseNotesProductId, useReleaseNotesQuery, } from "../legolandApi";
var defaultReleaseNotesStatus = {
    isDismissed: undefined,
    isRead: undefined,
};
export function useReleaseNotesStatus(_a) {
    var _b;
    var _c = _a === void 0 ? {} : _a, productId = _c.productId;
    productId = useReleaseNotesProductId(productId);
    var rnQuery = useReleaseNotesQuery({ productId: productId }, { refetchInterval: 10 * 60 * 1000 });
    var rnIds = useMemo(function () { var _a; return (_a = rnQuery.data) === null || _a === void 0 ? void 0 : _a.map(function (rn) { return rn.id; }); }, [rnQuery.data, rnQuery.isStale]);
    var seenIds = useMemo(function () {
        return productId && rnIds && getReleaseNotesStatusIds(productId, "seen", rnIds);
    }, [productId, rnIds]);
    var dismissedIds = useMemo(function () {
        return productId &&
            rnIds &&
            getReleaseNotesStatusIds(productId, "dismissed", rnIds);
    }, [productId, rnIds]);
    if (!rnIds || !seenIds || !dismissedIds) {
        return defaultReleaseNotesStatus;
    }
    if (rnQuery.error) {
        console.error(rnQuery.error);
        return defaultReleaseNotesStatus;
    }
    if (!((_b = rnQuery.data) === null || _b === void 0 ? void 0 : _b.length)) {
        return { isDismissed: true, isRead: true };
    }
    var isRead = rnIds.find(function (id) { return !seenIds.includes(id); }) === undefined;
    var isDismissed = isRead || rnIds.find(function (id) { return !dismissedIds.includes(id); }) === undefined;
    return { isDismissed: isDismissed, isRead: isRead };
}
export function useReleaseNotesMarkRead(_a) {
    var _b = _a === void 0 ? {} : _a, productId = _b.productId;
    var apiClient = useLegolandApiClient();
    var queryClient = useQueryClient();
    productId = useReleaseNotesProductId(productId);
    var markRead = useCallback(function (releaseNotes) {
        if (!productId)
            throw new Error("No product id provided.");
        setReleaseNotesStatus(productId, "seen", releaseNotes);
        queryClient.invalidateQueries(["legoland", "release-notes", productId]);
    }, [apiClient, productId]);
    return markRead;
}
export function useReleaseNotesMarkDismissed(_a) {
    var _b = _a === void 0 ? {} : _a, productId = _b.productId;
    var apiClient = useLegolandApiClient();
    var queryClient = useQueryClient();
    productId = useReleaseNotesProductId(productId);
    var markDismissed = useCallback(function (releaseNotes) {
        if (!productId)
            throw new Error("No product id provided.");
        setReleaseNotesStatus(productId, "dismissed", releaseNotes);
        queryClient.invalidateQueries(["legoland", "release-notes", productId]);
    }, [apiClient, productId]);
    return markDismissed;
}
var rootRnStorageKey = "legolandSdk.releaseNotes";
function getReleaseNotesStatusIds(productId, type, defaultIds) {
    var storageKey = "".concat(rootRnStorageKey, ".").concat(productId, ".").concat(type, "Ids");
    var idsJson = localStorage.getItem(storageKey);
    var ids;
    if (idsJson === null) {
        ids = defaultIds;
        // Do not save the state when there are no release notes. This is to prevent
        // notifying about new release notes when we first migrate to the new system.
        // The condition can be removed after the migration is complete.
        if (defaultIds.length > 0) {
            localStorage.setItem(storageKey, JSON.stringify(ids));
        }
    }
    else {
        ids = JSON.parse(idsJson);
    }
    return ids;
}
function setReleaseNotesStatus(productId, type, releaseNotes) {
    var e_1, _a;
    if (!releaseNotes.length)
        return; // The migration to the new system did not happen yet. Remove after migration is complete, so that new products receive proper notifications.
    var rnIds = releaseNotes.map(function (rn) { return rn.id; });
    var ids = getReleaseNotesStatusIds(productId, type, rnIds);
    try {
        for (var rnIds_1 = __values(rnIds), rnIds_1_1 = rnIds_1.next(); !rnIds_1_1.done; rnIds_1_1 = rnIds_1.next()) {
            var id = rnIds_1_1.value;
            if (!ids.includes(id)) {
                ids.push(id);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (rnIds_1_1 && !rnIds_1_1.done && (_a = rnIds_1.return)) _a.call(rnIds_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    localStorage.setItem("".concat(rootRnStorageKey, ".").concat(productId, ".").concat(type, "Ids"), JSON.stringify(ids));
}
//# sourceMappingURL=releaseNotesHooks.js.map